import { Interpolation, Theme, css } from '@emotion/react';
import dayjs from 'dayjs';
import { FirstWordBoldText } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { Photos } from '@frontend/photos';
import { theme } from '@frontend/theme';
import { Chip, Text, Truncated } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../constants';
import { ExtendedAppointment } from '../../types';
import { ColorDot } from './color-dot';

interface AppointmentListItemProps {
  as?: React.ElementType;
  containerCss?: Interpolation<Theme>;
  appointment: ExtendedAppointment;
}

export const AppointmentListItem = ({
  as: Component = 'section',
  containerCss,
  appointment,
}: AppointmentListItemProps) => {
  const { t } = useTranslation('schedule');

  const personFullName = appointment.person ? getFullName(appointment.person) || t('Unknown') : t('Unknown');
  return (
    <Component css={[mainContainer, containerCss]}>
      <Photos.ContactProfilePhoto
        size='small'
        personId={appointment.person?.personId ?? ''}
        name={personFullName}
        // TODO: integrate click handler
        onClick={() => console.log('Open Contact Panel')}
      />
      <div css={{ flex: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5), minWidth: 0 }}>
        <Truncated as={FirstWordBoldText} size='medium'>
          {personFullName}
        </Truncated>
        <div css={{ flex: 1, display: 'flex', gap: theme.spacing(0.5) }}>
          <div css={contentGridContainer}>
            <Truncated as={Text} size='small' color='subdued'>
              {dayjs(appointment.start).format('h:mm A')} ({appointment.duration} {t('Mins')})
            </Truncated>
            <Truncated as={Text} size='small' color='subdued'>
              {appointment.practitionerName || '-'}
            </Truncated>
            <div css={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center', minWidth: 0 }}>
              <ColorDot size='small' color={appointment.displayOptions?.primaryColor || PRIMARY_EVENT_COLOR} />
              <Truncated as={Text} size='small' color='subdued'>
                {appointment.type || '-'}
              </Truncated>
            </div>
            <Truncated as={Text} size='small' color='subdued'>
              {appointment.operatory || '-'}
            </Truncated>
          </div>
          <div css={{ display: 'flex', gap: theme.spacing(0.5) }}>
            {/* // TODO: integrate appintment-status & verification status component */}
            <Chip size='tiny' css={{ gap: 0 }} leftElement={<Icon name='pending' size={12} />}></Chip>
            <Chip variant='active' size='tiny' css={{ gap: 0 }} leftElement={<Icon name='shield' size={12} />}></Chip>
          </div>
        </div>
      </div>
    </Component>
  );
};

const mainContainer = css({
  display: 'flex',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
});

const contentGridContainer = css({
  flex: 1,
  display: 'grid',
  gridTemplateColumns: 'repeat(2, auto)',
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(0.5),
});
